import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import {
  BETWEEN,
  DATE,
  GTE,
  LIKE,
  LTE,
  STRING,
} from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import TextInput from "../../framework/TextInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import NumberInput from "../../framework/NumberInput";
import SelectInput from "../../framework/SelectInput";
import { statusOptions } from "../../utils/constant";
import { fetchBranches } from "../../services/branch-service";
import dayjs from "dayjs";
import DatePicker from "../../framework/date/DatePicker";
const MedicineWiseStockRequestReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  refreshData = false,
  reportRef,
}) => {
  const [requestStartDate, setRequestStartDate] = useState(
    dayjs().subtract(7, "day")
  );
  const [requestEndDate, setRequestEndDate] = useState(dayjs());
  const [product, setProduct] = useState("");
  const [branchSuggestions, setBranchSuggestions] = useState([]);
  const [selectedFromBranch, setSelectedFromBranch] = useState([]);
  const [selectedToBranch, setSelectedToBranch] = useState([]);
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(100);
  const filterRequestStartDateRef = useRef();
  const filterRequestEndDateRef = useRef();
  const filterProductNameRef = useRef();
  const filterFromBranchIdRef = useRef();
  const filterToBranchIdRef = useRef();
  const filterStatusRef = useRef();
  const filterLimitRef = useRef();
  const applyFilterRef = useRef();
  const [orderBy] = useState([
    { fieldName: "stockRequestDateTime", orderType: "desc" },
    { fieldName: "stockRequestDateTime", orderType: "desc" },
  ]);
  const { setCurrentComponent } = useContext(GlobalContext);
  const handleFetchBranches = useCallback((event) => {
    setBranchSuggestions([]);
    fetchBranches(`deleted=false`).then((resp) => {
      let updatedResp = resp.result.rows.map((branch) => ({
        id: branch._id,
        label: branch.code,
      }));
      setBranchSuggestions(updatedResp);
    });
  }, []);
  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      const _requestStartDate = requestStartDate?.format("DD/MM/YYYY");
      const _requestEndDate = requestEndDate?.format("DD/MM/YYYY");
      if (_requestStartDate && !_requestEndDate) {
        filters.push(
          createFilterParam("createdTime", _requestStartDate, DATE, GTE)
        );
      }
      if (!_requestStartDate && _requestEndDate) {
        filters.push(
          createFilterParam("createdTime", _requestEndDate, DATE, LTE)
        );
      }
      if (_requestStartDate && _requestEndDate) {
        filters.push(
          createFilterParam(
            "createdTime",
            { start: _requestStartDate, end: _requestEndDate },
            DATE,
            BETWEEN
          )
        );
      }
      if (!isEmpty(product)) {
        filters.push(createFilterParam("productName", product, "string", LIKE));
      }
      if (!isEmpty(status)) {
        filters.push(createFilterParam("status", status, "string", LIKE));
      }
      if (!isEmpty(selectedFromBranch)) {
        filters.push(
          createFilterParam(
            "fromBranchId",
            selectedFromBranch?.label,
            STRING,
            LIKE
          )
        );
      }
      if (!isEmpty(selectedToBranch)) {
        filters.push(
          createFilterParam("toBranchId", selectedToBranch?.label, STRING, LIKE)
        );
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;
      if (orderBy?.length) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }

      reportRef.current?.applyFilters(result);
    },
    [
      requestStartDate,
      requestEndDate,
      product,
      status,
      selectedFromBranch,
      selectedToBranch,
      orderBy,
      limit,
      reportRef,
    ]
  );
  const filterListener = useCallback((event) => {
    const key = event.key.toLowerCase();
    const ctrl = event.ctrlKey;
    if (ctrl && key === "enter") {
      event.preventDefault();
      return applyFilterRef.current.click();
    }
  }, []);
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="mwsr_filter_request_start_date"
            label="Stock Request Start Date"
            inputRef={filterRequestStartDateRef}
            onChange={(value) => setRequestStartDate(value)}
            onFocus={() =>
              setCurrentComponent("mwsr_filter_request_start_date")
            }
            value={requestStartDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "mwsr_filter_request_end_date",
                down: "mwsr_filter_request_end_date",
                left: "mwsr_filter_request_end_date",
                right: "mwsr_filter_request_end_date",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="mwsr_filter_request_end_date"
            label="Stock Request End Date"
            inputRef={filterRequestEndDateRef}
            onChange={(value) => setRequestEndDate(value)}
            onFocus={() => {
              executeScroll(filterRequestEndDateRef);
              setCurrentComponent("mwsr_filter_request_end_date");
            }}
            value={requestEndDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "mwsr_filter_request_start_date",
                down: "mwsr_filter_product_name",
                left: "mwsr_filter_request_start_date",
                right: "mwsr_filter_product_name",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <TextInput
            id="mwsr_filter_product_name"
            label="Product Name"
            ref={filterProductNameRef}
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            onFocus={() => {
              setCurrentComponent("mwsr_filter_product_name");
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "mwsr_filter_request_end_date",
                down: "mwsr_filter_from_branch",
                left: "mwsr_filter_request_end_date",
                right: "mwsr_filter_from_branch",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="mwsr_filter_from_branch"
            label="From Branch"
            value={selectedFromBranch}
            ref={filterFromBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedFromBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterFromBranchIdRef);
              setCurrentComponent("mwsr_filter_from_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "mwsr_filter_product_name",
              down: "mwsr_filter_to_branch",
              left: "mwsr_filter_product_name",
              right: "mwsr_filter_to_branch",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="mwsr_filter_to_branch"
            label="To Branch"
            value={selectedToBranch}
            ref={filterToBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedToBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterToBranchIdRef);
              setCurrentComponent("mwsr_filter_to_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "mwsr_filter_from_branch",
              down: "mwsr_filter_status",
              left: "mwsr_filter_from_branch",
              right: "mwsr_filter_status",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="mwsr_filter_status"
            label="Status"
            ref={filterStatusRef}
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={status}
            onChange={(e, option) => setStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterStatusRef);
              setCurrentComponent("mwsr_filter_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "mwsr_filter_to_branch",
              down: "mwsr_filter_limit",
              left: "mwsr_filter_to_branch",
              right: "mwsr_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="mwsr_filter_limit"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("mwsr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "mwsr_filter_status",
                down: "mwsr_apply_filter_button",
                left: "mwsr_filter_status",
                right: "mwsr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="mwsr_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            ref={applyFilterRef}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "mwsr_filter_limit",
              left: "mwsr_filter_limit",
              down: "mwsr_apply_filter_button",
              right: "mwsr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    handleFetchBranches();
  }, [handleFetchBranches]);
  useEffect(() => {
    if (refreshData) {
      applyFilterHandler(true);
    }
  }, [refreshData, applyFilterHandler]);
  useEffect(() => {
    if (onFilterShow) {
      filterProductNameRef?.current.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) document.addEventListener("keyup", filterListener, false);
    return () => {
      document.removeEventListener("keyup", filterListener, false);
    };
  }, [filterListener, onFilterShow]);

  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line
  }, [applyFilterOnMount]);

  return <Filters filterContent={filterContent} filterFooter={filterFooter} />;
};

export default MedicineWiseStockRequestReportFilter;
